import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AccountFormProps } from './types';
import { styles } from './styles';
import { Modal, Button, Input, Select, Spinner, Alert } from '@v2/components';
import { useRoles, useCreateAccount, useUpdateAccount } from '@v2/hooks';
import { AccountFormData } from '@v2/types';

const AccountForm: React.FC<AccountFormProps> = ({
  account,
  onCancel,
  onCompleted,
}) => {
  const isEditing = !!account;
  const title = isEditing ? 'Edit Account' : 'Create Account';

  const { roles, loading: rolesLoading } = useRoles();
  const { createAccount, loading: createLoading, error: createError } = useCreateAccount();
  const { updateAccount, loading: updateLoading, error: updateError } = useUpdateAccount();

  const mutationLoading = createLoading || updateLoading;
  const mutationError = createError || updateError;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<AccountFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      roleId: '',
      idpObjectId: '',
    },
  });

  useEffect(() => {
    if (account && roles.length > 0) {
      reset({
        firstName: account.firstName || '',
        lastName: account.lastName || '',
        email: account.email || '',
        roleId: String(account.roleId),
        idpObjectId: account.idpObjectId || '',
      });
    }
  }, [account, roles, reset]);

  const onSubmit = async (data: AccountFormData) => {
    if (isEditing && account) {
      const payload = {
        ...account,
        ...data,
        roleId: Number(data.roleId),
      };
      const success = await updateAccount(account.id, payload);
      if (success) {
        toast.success('Account updated successfully');
        onCompleted();
      }
    } else {
      const payload = {
        ...data,
        roleId: Number(data.roleId),
      };
      const newAccountId = await createAccount(payload);
      if (newAccountId) {
        toast.success('Account created successfully');
        onCompleted(newAccountId);
      }
    }
  };

  const handleReset = () => {
    reset();
  };

  const roleOptions = roles.map((role) => ({
    value: String(role.id),
    label: role.name.replace(/([a-z])([A-Z])/g, '$1 $2'),
  }));

  const isLoading = mutationLoading || isSubmitting;

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReset}
            disabled={!isDirty || isLoading}
          >
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || isLoading}
            isLoading={isLoading}
          >
            {isEditing ? 'Save Changes' : 'Create Account'}
          </Button>
        </>
      }
    >
      {rolesLoading ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
          <Spinner size="lg" className="tw-text-primary-600 tw-mb-3" />
          <span className="tw-text-neutral-500">Loading...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
          {mutationError && (
            <Alert variant="error">{mutationError}</Alert>
          )}

          <div className={styles.formRow}>
            <Input
              {...register('firstName', { required: 'First name is required' })}
              label="First Name"
              placeholder="First"
              required
              error={errors.firstName?.message}
            />

            <Input
              {...register('lastName', { required: 'Last name is required' })}
              label="Last Name"
              placeholder="Last"
              required
              error={errors.lastName?.message}
            />
          </div>

          <div className={styles.formRow}>
            <Input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              label="Email Address"
              placeholder="Email"
              type="email"
              required
              error={errors.email?.message}
            />

            <Select
              {...register('roleId', { required: 'Role is required' })}
              label="Role"
              placeholder="Select a role"
              options={roleOptions}
              required
              error={errors.roleId?.message}
            />
          </div>

          <div className={styles.fullWidthField}>
            <Input
              {...register('idpObjectId', { required: 'Azure ID is required' })}
              label="Azure ID"
              placeholder="(ie. 3470ff3b-1d66-418a-a7ac-547ce662d277)"
              required
              error={errors.idpObjectId?.message}
            />
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AccountForm;
